import React, { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import PerfectScrollbar from 'react-perfect-scrollbar'
import ReCAPTCHA from 'react-google-recaptcha'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { orderBy } from 'lodash'
import { Avatar, Box, Button, Grid, Typography } from '@material-ui/core'

import helpers from 'helpers'

import useSnackbar from 'hooks/useSnackbar'
import useConsent from 'hooks/useConsent'
import useAuth from 'hooks/useAuth'

import theme from 'theme'
import useStyles from './styles'
import * as service from 'service'
import constants from 'constants/index'
import { getBaseURL, getGoogleRecaptchaToken } from 'service/env'
import JoditEditor from 'components/JoditEditor'

const Preview = ({
  isAccepted = false,
  refresh = () => {},
  isEditConsent = false,
}) => {
  const [recaptcha, setRecaptcha] = useState('')

  const {
    nextStep,
    toNextStep,
    setToNextStep,
    dataConsentForm,
    setDataConsentForm,
    setIsConsentLoading,
  } = useConsent()
  const { company } = useAuth()
  const recaptchaRef = useRef(null)

  const classes = useStyles()
  const snackbar = useSnackbar()

  const logoPath =
    company?.logo?.url && `${getBaseURL('dponet')}${company?.logo?.url}`

  const components = orderBy(
    Object.entries(dataConsentForm?.components || {}).filter(
      ([key]) => key !== constants.consent.NOT_AUTHORIZED_CONSENT_LABEL,
    ),
    ([_, item]) => item.order,
  )

  const { handleSubmit, errors } = useForm()

  const expiredRecaptcha = () => setRecaptcha('')

  const handleRecaptcha = (token) => token && setRecaptcha(token)

  const avatarLetters = (company) =>
    (company?.name || 'Empresa cliente')
      .split(' ')
      .map((word) => word[0])
      .join('')

  const onSubmit = async () => {
    if (isAccepted) {
      handleCreateAccept()
    } else {
      nextStep()
      setToNextStep(false)
    }
  }

  const handleCreateAccept = async () => {
    setIsConsentLoading(true)

    try {
      if (isEditConsent) {
        await service.dponet.consent.update({
          consentFormId: dataConsentForm?.id,
          components: JSON.stringify(dataConsentForm?.components),
          buttonText: dataConsentForm?.buttonText,
          buttonTextNotAuthorized: dataConsentForm?.buttonTextNotAuthorized,
          not_authorized_button: dataConsentForm?.not_authorized_button,
        })
      } else {
        const creationResponse = await service.dponet.consent.create({
          consentForm: {
            ...dataConsentForm,
            emailConfirmation: dataConsentForm?.emailConfirmation || false,
            emailResponsible: dataConsentForm?.emailResponsible || false,
            components: JSON.stringify(dataConsentForm?.components),
            updateType: 'new_version',
          },
        })
        const token = creationResponse?.data?.token

        await setDataConsentForm((prevValue) => ({
          ...prevValue,
          token,
        }))
      }

      snackbar.open({
        message: `${
          isEditConsent ? 'Formulário atualizado' : 'Aceite criado'
        } com sucesso'`,
        variant: 'success',
      })
      refresh()
      if (!isEditConsent) {
        nextStep()
        setToNextStep(false)
      }
    } catch (error) {
      console.error(error)
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          `Ocorreu uma falha ao tentar ${
            isEditConsent ? 'editar o formulário' : 'criar o aceite'
          }!`,
        variant: 'error',
      })
    } finally {
      setIsConsentLoading(false)
    }
  }

  useEffect(() => {
    if (toNextStep) {
      handleSubmit(onSubmit)()
      if (errors) setToNextStep(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toNextStep])

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      id={constants.consent.NEW_CONSENT_FORM_DRIVER_STEP_3}
    >
      <Box className={classes.headerBox}>
        <Typography variant="h3" align="center" className={classes.title}>
          {dataConsentForm?.title}
        </Typography>
        {dataConsentForm?.subtitle && (
          <Typography variant="h4" align="center" className={classes.title}>
            {dataConsentForm?.subtitle}
          </Typography>
        )}
      </Box>
      <Box p={5} className={classes.contentBox}>
        <Grid container spacing={5}>
          <Grid item xs={12}>
            <Box
              display="flex"
              justifyContent="start"
              alignItems="center"
              width="100%"
              height="100%"
            >
              <Box marginRight={3}>
                <Avatar
                  className={classes.avatar}
                  src={logoPath}
                  variant="rounded"
                  style={{ borderRadius: logoPath ? 0 : 24 }}
                >
                  {avatarLetters(company)[0]}
                  {avatarLetters(company)[avatarLetters(company).length - 1]}
                </Avatar>
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                height="100%"
              >
                <Typography variant="h5" className={classes.title}>
                  {company?.name}
                </Typography>
                <Typography variant="h5" className={classes.title}>
                  {company?.companyName}
                </Typography>
                <Box display="flex">
                  <Typography
                    variant="h5"
                    className={clsx(classes.document, classes.title)}
                  >
                    {company?.cnpj ? 'CNPJ: ' : 'CPF: '}
                    <strong>
                      {helpers.formatters.document.identify(
                        company?.cnpj || company?.cpf,
                      )}
                    </strong>
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box py={2} pl={2} className={classes.descriptionBox}>
              <PerfectScrollbar
                options={{ wheelPropagation: false, useBothWheelAxes: true }}
                className={classes.scrollBar}
              >
                <Box pr={2}>
                  <JoditEditor.Preview
                    allowIframe
                    content={dataConsentForm?.content}
                  />
                </Box>
              </PerfectScrollbar>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box
              display="flex"
              flexDirection="column"
              gridGap={theme.spacing(2)}
            >
              {components.map(([key, value]) => {
                const isEmailField =
                  key === constants.consent.DEFAULT_EMAIL_ITEM_PROPS.name

                return (
                  <Box key={key}>
                    <Box display="flex" flexDirection="column" width="100%">
                      <Typography variant="h6" className={classes.labelButton}>
                        {isEmailField ? value?.label || key : key}
                        {value?.required && (
                          <span className={classes.requiredSpan}> *</span>
                        )}
                      </Typography>
                      <Typography
                        variant="caption"
                        className={classes.labelDescription}
                      >
                        {value?.subtitle}
                      </Typography>
                      <input
                        type="text"
                        className={classes.inputText}
                        {...(isEmailField && {
                          placeholder: value?.placeholder,
                        })}
                        disabled
                      />
                    </Box>
                  </Box>
                )
              })}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" gridGap={theme.spacing(2)} alignItems="center">
              <ReCAPTCHA
                ref={recaptchaRef}
                theme="light"
                sitekey={getGoogleRecaptchaToken()}
                onChange={handleRecaptcha}
                onExpired={expiredRecaptcha}
              />
              <Box
                width="100%"
                display="flex"
                gridGap={2}
                flexDirection={{ xs: 'column', sm: 'row' }}
              >
                {dataConsentForm?.components?.[
                  constants.consent.NOT_AUTHORIZED_CONSENT_LABEL
                ] && (
                  <Button
                    className={classes.notAuthorizeButton}
                    disabled={!recaptcha}
                    variant="contained"
                    size="large"
                    fullWidth
                  >
                    <Typography variant="h5" noWrap>
                      {dataConsentForm?.buttonTextNotAuthorized ||
                        'EU NÃO AUTORIZO'}
                    </Typography>
                  </Button>
                )}
                <Button
                  className={classes.authorizeButton}
                  disabled={!recaptcha}
                  variant="contained"
                  size="large"
                  fullWidth
                >
                  <Typography variant="h5" noWrap>
                    {dataConsentForm?.buttonText ?? 'EU AUTORIZO'}
                  </Typography>
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}

Preview.propTypes = {
  isAccepted: PropTypes.bool,
  setLoading: PropTypes.func,
  refresh: PropTypes.func,
  isEditConsent: PropTypes.bool,
}

export default Preview
